import { useState } from "react"
import { AxiosError } from "axios"
import { useNavigate } from "react-router"
import { useQueryClient } from "@tanstack/react-query"

import { Toast } from "../../components/Toast"
import WizardContactPreferencesForm from "../../components/WizardContactPreferencesForm"
import GovtWizardTakeActionImplementations from "./GovtWizardTakeActionImplementations"
import GovtWizardTakeActionNextUp from "./GovtWizardTakeActionNextUp"
import {
  useAccountId,
  useAccountUrlPrefix,
  useUpdateGovtAppWizard,
} from "../../hooks"
import { genericErrMsg } from "../../api/auth"
import { useGovernmentStore } from "../../stores/useGovernmentStore"
import { AccountTypes } from "@/types/account"
import { Profile } from "@/types"
import { GovtApWizardType } from "@/types/govtAppWizard"

export interface GovtWizardContentTakeActionProps {
  account: AccountTypes | undefined
  profile: Profile | undefined
  govtAppWizard: GovtApWizardType | undefined
  handlePrevious: () => void
  handleNext: () => void
}

const GovtWizardContentTakeAction = ({
  account,
  profile,
  handlePrevious,
  handleNext,
}: GovtWizardContentTakeActionProps) => {
  const accountId = useAccountId()
  const accountUrlPrefix = useAccountUrlPrefix()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const [isYesBtn, setIsYesBtn] = useState<boolean>(false)

  const [pageView, setPageView] = useState<
    "implementation" | "contact" | "nextup"
  >("implementation")

  const { actionKeys, management, bookConsult, setBookConsult } =
    useGovernmentStore()

  const {
    mutateAsync: updateGovtAppWizard,
    isPending: isPendingUpdateGovtAppWizard,
  } = useUpdateGovtAppWizard(queryClient, accountId, {
    onSuccess: () => {
      if (account?.is_member) {
        setPageView("nextup")
      } else if (!account?.is_member && management === "self" && bookConsult) {
        setTimeout(() => {
          navigate(`${accountUrlPrefix}/`)
        }, 0)
      } else {
        setTimeout(() => {
          navigate(`${accountUrlPrefix}/checkup/government/paywall`)
        }, 0)
      }
    },
    onError: (error: AxiosError) => {
      Toast.error(error?.message || genericErrMsg)
    },
  })

  const handleSubmit = async (ncxServices?: boolean) => {
    const implementation = bookConsult || ncxServices ? "ncx_services" : "self"

    if (ncxServices && !bookConsult) {
      setBookConsult(true)
    }

    setIsYesBtn(!!ncxServices)

    const postData = {
      wizard_key: "govt_application",
      action_keys: actionKeys,
      implementation: implementation,
    }

    const isNcxServices = account?.is_member ? !!ncxServices : bookConsult

    if (
      pageView === "implementation" &&
      profile?.preferred_contact_method.length === 0 &&
      isNcxServices
    ) {
      setPageView("contact")
    } else {
      await updateGovtAppWizard(postData)
    }
  }

  const handleResetContactView = () => {
    setPageView("implementation")
  }

  if (pageView === "contact") {
    return (
      <div className="pt-10 lg:pt-13 pb-[202px] sm:pb-31">
        <WizardContactPreferencesForm
          profile={profile}
          handleResetContactView={handleResetContactView}
          handleSubmit={() => {
            handleSubmit()
          }}
          isPending={isPendingUpdateGovtAppWizard}
          componentViewTrackingClass="componentview-govt-app-page-4-contact-form"
          goBackBtnTrackingClass="govt-app-page-4-contact-go-back-button"
          saveBtnTrackingClass="govt-app-page-4-contact-save-and-continue-button"
        />
      </div>
    )
  }

  if (pageView === "nextup") {
    return (
      <GovtWizardTakeActionNextUp
        handleResetContactView={handleResetContactView}
        handleNext={handleNext}
      />
    )
  }

  return (
    <GovtWizardTakeActionImplementations
      account={account}
      handlePrevious={handlePrevious}
      handleSubmit={(e) => {
        handleSubmit(e)
      }}
      isYesBtn={isYesBtn}
      isPending={isPendingUpdateGovtAppWizard}
    />
  )
}

export default GovtWizardContentTakeAction
